import { Box, Button, Checkbox, Flex, Image, Input, Text, Textarea, Select, FormControl, FormLabel, VStack, Divider, useColorModeValue } from "@chakra-ui/react"
import { AppMeta } from "@xnav/types/src/common"
import { useMemo, useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCancel, faCheck, faCross, faUpload } from "@fortawesome/free-solid-svg-icons"
import { nanoid } from 'nanoid'
import ImageCropper from "./ImageCropper"
import CustomDropdown from "./customDropdown"
import { apiGetRequest, apiPutRequest } from "@/functions/requests"
import { useAuthenticator } from "@aws-amplify/ui-react"

const AppForm: React.FC<{
	app: AppMeta,
	onValidate: (newApp: AppMeta) => string | undefined,
	onAdd: (newApp: AppMeta, icon: File | undefined) => void,
	onCancel: () => void,
	userRole?: string | null
}> = ({ app, onAdd, onValidate, onCancel, userRole }) => {

	const { user } = useAuthenticator()
	const currentUserEmail = user?.signInDetails?.loginId || ''

	const [newApp, setNewApp] = useState(app)
	const [file, setFile] = useState<File>()
	const [uncroppedImage, setUncroppedImage] = useState<string | null>(null)
	const [collabInput, setCollabInput] = useState('')
	const [reviewerInput, setReviewerInput] = useState('')
	const [tagInput, setTagInput] = useState('')
	const [privateAccessType, setPrivateAccessType] = useState<'domain' | 'code'>(app.hiddenCode ? 'code' : 'domain')
	const [organizations, setOrganizations] = useState<string[]>([])

	useEffect(() => {
		// 1. Fetch unique orgs from users list (Admins)
		apiGetRequest<undefined, { org: string }[]>('/users').then((users) => {
			if (users && users.length > 0) {
				const uniqueOrgs = Array.from(new Set(users.map((u) => u.org).filter(Boolean)));
				setOrganizations(prev => Array.from(new Set([...prev, ...uniqueOrgs])));
			}
		}).catch(console.error);

		// 2. Fallback/Addition: Fetch unique orgs from existing apps (all users)
		apiGetRequest<undefined, AppMeta[]>('/apps').then((apps) => {
			if (apps && apps.length > 0) {
				const uniqueOrgs = Array.from(new Set(apps.map((a) => a.org).filter(Boolean)));
				setOrganizations(prev => Array.from(new Set([...prev, ...uniqueOrgs])));
			}
		}).catch(console.error);

		// 3. Fetch custom orgs from database
		apiGetRequest<undefined, { orgs?: string[] }>('/users/custom_organizations').then((data) => {
			if (data && data.orgs) {
				setOrganizations(prev => Array.from(new Set([...prev, ...data.orgs!])));
			}
		}).catch(console.error);
	}, []);

	useEffect(() => {
		if (currentUserEmail && (!newApp.org || newApp.org === '')) {
			apiGetRequest<undefined, { org: string }>(`/users/${encodeURIComponent(currentUserEmail)}`).then((userData) => {
				if (userData && userData.org) {
					setNewApp(prev => ({
						...prev,
						org: prev.org || userData.org
					}));
				}
			}).catch(console.error);
		}
	}, [currentUserEmail]);

	const invalid = useMemo(() => {
		if (!newApp) return undefined;
		if (!newApp.name || newApp.name.trim() === '') return "App Name is required";
		if (!newApp.creator || newApp.creator.trim() === '') return "Creator Name is required";
		return onValidate(newApp);
	}, [newApp])

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const reader = new FileReader();
			reader.addEventListener('load', () => setUncroppedImage(reader.result?.toString() || null));
			reader.readAsDataURL(e.target.files[0]);
		}
	}

	const handleCropComplete = (croppedFile: File) => {
		setFile(croppedFile);
		setNewApp({
			...newApp,
			iconUrl: URL.createObjectURL(croppedFile)
		});
		setUncroppedImage(null);
	}

	const bgForm = useColorModeValue("white", "#131b26");
	const bgInput = useColorModeValue("white", "#0d131f");
	const borderInput = useColorModeValue("gray.200", "whiteAlpha.200");
	const textInput = useColorModeValue("gray.900", "gray.100");
	const labelColor = useColorModeValue("gray.700", "gray.200");
	const subtextColor = useColorModeValue("gray.500", "gray.400");

	if (uncroppedImage) {
		return (
			<Flex flexDir="column" p={6} bg={bgForm} borderRadius="xl" shadow="md">
				<Text fontSize="lg" fontWeight="bold" mb={4} color={labelColor}>Crop App Icon</Text>
				<ImageCropper 
					imageSrc={uncroppedImage} 
					onCropComplete={handleCropComplete} 
					onCancel={() => setUncroppedImage(null)} 
				/>
			</Flex>
		)
	}

	return (
		<VStack spacing={6} align="stretch" p={2}>
			<FormControl isRequired>
				<FormLabel fontWeight="bold" color={labelColor}>App Name</FormLabel>
				<Input
					w="100%"
					placeholder="Enter your course name..."
					value={newApp.name}
					onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
					fontSize="md"
					borderRadius="lg"
					boxShadow="sm"
					bg={bgInput}
					color={textInput}
					borderColor={borderInput}
					_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
				/>
			</FormControl>

			<FormControl isRequired>
				<FormLabel fontWeight="bold" color={labelColor}>Instructor / Creator Name</FormLabel>
				<Input
					w="100%"
					placeholder="Enter your name..."
					value={newApp.creator || ''}
					onChange={(e) => setNewApp({ ...newApp, creator: e.target.value })}
					fontSize="md"
					borderRadius="lg"
					boxShadow="sm"
					bg={bgInput}
					color={textInput}
					borderColor={borderInput}
					_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
				/>
			</FormControl>

			<FormControl>
				<FormLabel fontWeight="bold" color={labelColor}>Organization Name</FormLabel>
				<CustomDropdown
					options={organizations}
					value={newApp.org || ''} 
					onChange={(val) => setNewApp({ ...newApp, org: val })}
					placeholder="Select or type your organization (e.g. Samsung)"
					bg={bgInput}
					color={textInput}
					borderColor={borderInput}
					borderRadius="lg"
					boxShadow="sm"
				/>
			</FormControl>

			<FormControl>
				<FormLabel fontWeight="bold" color={labelColor}>Description</FormLabel>
				<Textarea 
					placeholder="Briefly describe what this course is about..."
					value={newApp.description} 
					onChange={e => setNewApp({ ...newApp, description: e.target.value })}
					borderRadius="lg"
					boxShadow="sm"
					rows={4}
					bg={bgInput}
					color={textInput}
					borderColor={borderInput}
					_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
				/>
			</FormControl>

			<FormControl>
				<FormLabel fontWeight="bold" color={labelColor}>App Icon</FormLabel>
				<Flex gap={4} alignItems="center">
					{newApp.iconUrl ? (
						<Box borderRadius="full" overflow="hidden" w="80px" h="80px" shadow="md" border="2px solid" borderColor={borderInput}>
							<Image boxSize="100%" objectFit="cover" src={newApp.iconUrl} alt="App Icon" />
						</Box>
					) : (
						<Flex borderRadius="full" w="80px" h="80px" bg={useColorModeValue("gray.100", "whiteAlpha.100")} alignItems="center" justifyContent="center" shadow="sm">
							<Text fontSize="xs" color="gray.400" textAlign="center">No Icon</Text>
						</Flex>
					)}
					<Button as="label" htmlFor="icon-upload" variant="outline" color={useColorModeValue("#0284c7", "#38bdf8")} borderColor={useColorModeValue("gray.300", "whiteAlpha.300")} _hover={{ bg: useColorModeValue("blue.50", "whiteAlpha.100") }} leftIcon={<FontAwesomeIcon icon={faUpload} />} cursor="pointer" borderRadius="full">
						Choose Image
						<Input id="icon-upload" type="file" display="none" onChange={handleFileSelect} accept="image/*" />
					</Button>
				</Flex>
			</FormControl>

			<Divider borderColor={useColorModeValue("gray.200", "whiteAlpha.150")} />

			<FormControl>
				<FormLabel fontWeight="bold" color={labelColor}>Course Visibility</FormLabel>
				<Select 
					value={newApp.visibility || 'organization'} 
					onChange={(e) => setNewApp({ ...newApp, visibility: e.target.value as 'public' | 'organization' })}
					borderRadius="lg"
					boxShadow="sm"
					bg={bgInput}
					color={textInput}
					borderColor={borderInput}
					_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
				>
					<option value="organization" style={{ background: '#131b26', color: '#f3f4f6' }}>Private (Restricted Access)</option>
					<option value="public" style={{ background: '#131b26', color: '#f3f4f6' }}>Public (Everyone)</option>
				</Select>
			</FormControl>

			{(newApp.visibility === 'organization' || !newApp.visibility) && (
				<Box p={4} bg={useColorModeValue("blue.50", "rgba(14, 165, 233, 0.05)")} borderRadius="xl" border="1px dashed" borderColor={useColorModeValue("blue.200", "whiteAlpha.200")}>
					<FormControl mb={4}>
						<FormLabel fontWeight="bold" color={labelColor} fontSize="sm">Access Method</FormLabel>
						<Select 
							value={privateAccessType} 
							onChange={(e) => setPrivateAccessType(e.target.value as 'domain' | 'code')}
							bg={bgInput}
							color={textInput}
							borderColor={borderInput}
						>
							<option value="domain" style={{ background: '#131b26', color: '#f3f4f6' }}>Option 1: Allow users by Email Domain</option>
							<option value="code" style={{ background: '#131b26', color: '#f3f4f6' }}>Option 2: Hidden Access Code</option>
						</Select>
					</FormControl>

					{privateAccessType === 'domain' ? (
						<FormControl>
							<FormLabel fontWeight="bold" color={labelColor} fontSize="sm">Allowed Email Domain</FormLabel>
							<Input
								w="100%"
								placeholder="e.g. @samsung.com"
								value={newApp.orgId || ''}
								onChange={(e) => setNewApp({ ...newApp, orgId: e.target.value, hiddenCode: undefined })}
								bg={bgInput}
								color={textInput}
								borderColor={borderInput}
								_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
							/>
							<Text fontSize="xs" color={subtextColor} mt={1}>Users with an email ending in this domain will automatically have access to this course.</Text>
						</FormControl>
					) : (
						<FormControl isInvalid={onValidate(newApp) === "Access code must be unique across all courses"}>
							<FormLabel fontWeight="bold" color={labelColor} fontSize="sm">Hidden Access Code</FormLabel>
							<Flex gap={2}>
								<Input
									placeholder="e.g. SECRET123"
									value={newApp.hiddenCode || ''}
									onChange={(e) => setNewApp({ ...newApp, hiddenCode: e.target.value, orgId: undefined })}
									bg={bgInput}
									color={textInput}
									borderColor={borderInput}
									_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
								/>
								<Button
									bg="#0ea5e9"
									color="white"
									_hover={{ bg: "#0284c7" }}
									onClick={() => {
										setNewApp({ ...newApp, hiddenCode: nanoid(8), orgId: undefined });
									}}
								>
									Generate
								</Button>
							</Flex>
							{onValidate(newApp) === "Access code must be unique across all courses" ? (
								<Text fontSize="xs" color="red.500" mt={1} fontWeight="semibold">
									This access code is already in use by another course. Please choose a unique code.
								</Text>
							) : (
								<Text fontSize="xs" color={subtextColor} mt={1}>
									Users must type this exact code in the mobile app to unlock the course.
								</Text>
							)}
						</FormControl>
					)}
				</Box>
			)}

			{(userRole === 'Super Admin' || userRole === 'Admin') && (
				<Box p={4} bg={useColorModeValue("orange.50", "rgba(245, 158, 11, 0.05)")} borderRadius="xl" border="1px dashed" borderColor={useColorModeValue("orange.200", "whiteAlpha.200")} mb={2}>
					<Text fontWeight="bold" color={useColorModeValue("orange.700", "orange.300")} mb={3}>{userRole === 'Super Admin' ? 'Super Admin Controls' : 'Admin Controls'}</Text>
					<VStack spacing={4}>
						<FormControl>
							<FormLabel fontWeight="bold" color={labelColor} fontSize="sm">Force Assign to Organization / Domain</FormLabel>
							<CustomDropdown
								options={organizations}
								value={newApp.orgId || ''} 
								onChange={(val) => setNewApp({ ...newApp, orgId: val })}
								placeholder="e.g. @samsung.com (Leave empty for none)"
								borderRadius="lg"
								boxShadow="sm"
								bg={bgInput}
								color={textInput}
								borderColor={borderInput}
							/>
						</FormControl>
						
						{userRole === 'Super Admin' && (
							<FormControl>
								<FormLabel fontWeight="bold" color={labelColor} fontSize="sm">Transfer Course Ownership (Creator ID)</FormLabel>
								<Input 
									value={newApp.creatorId || ''} 
									onChange={(e) => setNewApp({ ...newApp, creatorId: e.target.value })}
									placeholder="Enter User ID of the new owner..."
									borderRadius="lg"
									boxShadow="sm"
									bg={bgInput}
									color={textInput}
									borderColor={borderInput}
									_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
									fontFamily="monospace"
								/>
								<Text fontSize="xs" color={subtextColor} mt={1}>Changing this transfers the course so the new user can edit it.</Text>
							</FormControl>
						)}
					</VStack>
				</Box>
			)}

			<FormControl>
				<FormLabel fontWeight="bold" color={labelColor}>Collaborators</FormLabel>
				<Text fontSize="xs" color={subtextColor} mb={2}>Enter the email address or User ID of colleagues who can edit this course.</Text>
				<Flex gap={2} flexWrap="wrap" mb={2}>
					{newApp.collaborators?.map((collab, idx) => (
						<Box key={idx} bg={useColorModeValue("blue.100", "rgba(14, 165, 233, 0.15)")} color={useColorModeValue("blue.700", "#38bdf8")} px={3} py={1} borderRadius="full" fontSize="sm" display="flex" alignItems="center" gap={2} border="1px solid" borderColor={useColorModeValue("blue.200", "rgba(14, 165, 233, 0.3)")}>
							{collab}
							<FontAwesomeIcon 
								icon={faCross} 
								style={{ cursor: 'pointer', opacity: 0.6 }} 
								onClick={() => setNewApp(prev => ({ ...prev, collaborators: prev.collaborators?.filter((_, i) => i !== idx) }))}
							/>
						</Box>
					))}
				</Flex>
				<Input
					value={collabInput}
					onChange={e => setCollabInput(e.target.value)}
					placeholder="Type email or User ID and press Enter..."
					borderRadius="lg"
					boxShadow="sm"
					bg={bgInput}
					color={textInput}
					borderColor={borderInput}
					_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							const val = collabInput.trim().toLowerCase();
							if (val && !newApp.collaborators?.includes(val)) {
								setNewApp(prev => ({ ...prev, collaborators: [...(prev.collaborators || []), val] }));
								setCollabInput('');
							}
						}
					}}
				/>
			</FormControl>

			<FormControl>
				<FormLabel fontWeight="bold" color={labelColor}>Reviewers</FormLabel>
				<Text fontSize="xs" color={subtextColor} mb={2}>Enter the email address or User ID of colleagues who can review this course.</Text>
				<Flex gap={2} flexWrap="wrap" mb={2}>
					{newApp.reviewers?.map((reviewer, idx) => (
						<Box key={idx} bg={useColorModeValue("green.100", "rgba(16, 185, 129, 0.15)")} color={useColorModeValue("green.700", "#34d399")} px={3} py={1} borderRadius="full" fontSize="sm" display="flex" alignItems="center" gap={2} border="1px solid" borderColor={useColorModeValue("green.200", "rgba(16, 185, 129, 0.3)")}>
							{reviewer}
							<FontAwesomeIcon 
								icon={faCross} 
								style={{ cursor: 'pointer', opacity: 0.6 }} 
								onClick={() => setNewApp(prev => ({ ...prev, reviewers: prev.reviewers?.filter((_, i) => i !== idx) }))}
							/>
						</Box>
					))}
				</Flex>
				<Input
					value={reviewerInput}
					onChange={e => setReviewerInput(e.target.value)}
					placeholder="Type email or User ID and press Enter..."
					borderRadius="lg"
					boxShadow="sm"
					bg={bgInput}
					color={textInput}
					borderColor={borderInput}
					_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							const val = reviewerInput.trim().toLowerCase();
							if (val && !newApp.reviewers?.includes(val)) {
								setNewApp(prev => ({ ...prev, reviewers: [...(prev.reviewers || []), val] }));
								setReviewerInput('');
							}
						}
					}}
				/>
			</FormControl>

			<FormControl>
				<FormLabel fontWeight="bold" color={labelColor}>Course Tags</FormLabel>
				<Flex gap={2} flexWrap="wrap" mb={2}>
					{newApp.tags?.map((tag, idx) => (
						<Box key={idx} bg={useColorModeValue("purple.100", "rgba(168, 85, 247, 0.15)")} color={useColorModeValue("purple.700", "#c084fc")} px={3} py={1} borderRadius="full" fontSize="sm" display="flex" alignItems="center" gap={2} border="1px solid" borderColor={useColorModeValue("purple.200", "rgba(168, 85, 247, 0.3)")}>
							#{tag}
							<FontAwesomeIcon 
								icon={faCross} 
								style={{ cursor: 'pointer', opacity: 0.6 }} 
								onClick={() => setNewApp(prev => ({ ...prev, tags: prev.tags?.filter((_, i) => i !== idx) }))}
							/>
						</Box>
					))}
				</Flex>
				<Input
					value={tagInput}
					onChange={e => setTagInput(e.target.value)}
					placeholder="Type a tag and press Enter..."
					borderRadius="lg"
					boxShadow="sm"
					bg={bgInput}
					color={textInput}
					borderColor={borderInput}
					_focus={{ borderColor: "#0ea5e9", boxShadow: "0 0 0 1px #0ea5e9" }}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							const val = tagInput.trim().toLowerCase();
							if (val && !newApp.tags?.includes(val)) {
								setNewApp(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
								setTagInput('');
							}
						}
					}}
				/>
			</FormControl>

			<Flex gap={4} pt={4}>
				<Button flex={1} size="lg" borderRadius="xl" variant='outline' borderColor={useColorModeValue("gray.300", "whiteAlpha.200")} color={useColorModeValue("gray.600", "gray.300")} _hover={{ bg: useColorModeValue("gray.100", "whiteAlpha.100") }} onClick={onCancel} leftIcon={<FontAwesomeIcon icon={faCancel} />}>
					Cancel
				</Button>
				<Button
					flex={1}
					size="lg"
					borderRadius="xl"
					colorScheme={invalid ? 'red' : 'blue'}
					bgGradient={invalid ? undefined : "linear(to-r, #0284c7, #0ea5e9)"}
					_hover={invalid ? undefined : { bgGradient: "linear(to-r, #0369a1, #0284c7)", transform: "translateY(-1px)", shadow: "md" }}
					color="white"
					isDisabled={invalid !== undefined}
					onClick={async () => {
						let finalApp = { ...newApp };
						if (!finalApp.visibility) {
							finalApp.visibility = (finalApp.org || finalApp.orgId || finalApp.hiddenCode) ? 'organization' : 'public';
						}
						if (collabInput.trim()) {
							const val = collabInput.trim().toLowerCase();
							if (!finalApp.collaborators?.includes(val)) {
								finalApp.collaborators = [...(finalApp.collaborators || []), val];
							}
						}
						if (reviewerInput.trim()) {
							const val = reviewerInput.trim().toLowerCase();
							if (!finalApp.reviewers?.includes(val)) {
								finalApp.reviewers = [...(finalApp.reviewers || []), val];
							}
						}
						if (tagInput.trim()) {
							const val = tagInput.trim().toLowerCase();
							if (!finalApp.tags?.includes(val)) {
								finalApp.tags = [...(finalApp.tags || []), val];
							}
						}

						// Auto-sync new organization to custom_organizations in backend
						if (finalApp.org && finalApp.org.trim() !== "") {
							const trimmedOrg = finalApp.org.trim();
							const isNewOrg = !organizations.some(o => o.toLowerCase() === trimmedOrg.toLowerCase()) &&
											trimmedOrg.toLowerCase() !== "global" &&
											trimmedOrg.toLowerCase() !== "none";
							if (isNewOrg) {
								try {
									const data = await apiGetRequest<undefined, { orgs?: string[] }>('/users/custom_organizations');
									const latestOrgs = (data && data.orgs) ? data.orgs : [];
									if (!latestOrgs.some(o => o.toLowerCase() === trimmedOrg.toLowerCase())) {
										const updatedOrgs = [...latestOrgs, trimmedOrg];
										await apiPutRequest('/users/custom_organizations', { orgs: updatedOrgs });
									}
								} catch (e) {
									console.error("Failed to auto-register new organization from course form", e);
								}
							}
						}

						onAdd(finalApp, file);
					}}
					leftIcon={<FontAwesomeIcon icon={invalid ? faCancel: faCheck} />}
					shadow="sm"
				>
					{invalid ?? 'Confirm & Save'}
				</Button>
			</Flex>
		</VStack>
	)
}

export default AppForm
